/**
 * RecordPager — "4 of 25", Prev, Next: moving between the rows of a LIST while staying inside
 * the record you opened out of it.
 *
 * Reviewing alerts and events is a sweep, not a single lookup: an operator opens the first row
 * and works down the page. Without this, every row costs a close, a hunt for where they were,
 * and a click — so the detail view carries the list's cursor with it, and the keyboard moves it.
 *
 * Deliberately does NOT wrap at the ends. The list is one PAGE of a larger set, so wrapping
 * from the last row to the first reads as "it jumped" rather than "the page ended".
 */
import { useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * Marks a subtree that owns the arrow keys while the focus is inside it. Put it on anything
 * that ALSO navigates with left/right — an image carousel — so the two never both move.
 */
export const ARROW_SCOPE_ATTR = 'data-arrow-scope';

/** True while a text field, or an arrow-owning subtree, holds the focus. */
function arrowsAreTaken(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return true;
  return !!el.closest?.(`[${ARROW_SCOPE_ATTR}]`);
}

export interface UseRecordArrowKeysOptions {
  index: number;
  count: number;
  onIndexChange: (index: number) => void;
  /** False while the detail view is closed, so a background list doesn't answer the keyboard. */
  active?: boolean;
}

/**
 * Left/Right move the record cursor — unless something nearer the focus already claims them
 * (a text field being typed in, or a carousel the reader has clicked into: once they're
 * flicking through THIS event's snapshots, the same key must not throw them onto the next
 * event). Bound at the window so it works before anything inside has been focused.
 */
export function useRecordArrowKeys({ index, count, onIndexChange, active = true }: UseRecordArrowKeysOptions) {
  useEffect(() => {
    if (!active || count <= 1) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (arrowsAreTaken()) return;
      const next = e.key === 'ArrowLeft' ? index - 1 : index + 1;
      if (next < 0 || next > count - 1) return;
      e.preventDefault();
      onIndexChange(next);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, index, count, onIndexChange]);
}

export interface RecordPagerProps {
  index: number;
  count: number;
  onIndexChange: (index: number) => void;
  /** Plural noun for the counter — "events", "alerts". */
  noun?: string;
  /** Bind the arrow keys as well as rendering the buttons. */
  keyboard?: boolean;
  /**
   * `boxed` — the arrows and the count in ONE bordered group, for a record header where the
   * pager sits beside other large buttons and three separate boxes read as three controls.
   * It drops the noun: in a record's own header the reader knows what is being paged.
   */
  variant?: 'default' | 'boxed';
  className?: string;
}

export default function RecordPager({
  index,
  count,
  onIndexChange,
  noun = 'records',
  keyboard = true,
  variant = 'default',
  className = '',
}: RecordPagerProps) {
  useRecordArrowKeys({ index, count, onIndexChange, active: keyboard });

  const atStart = index <= 0;
  const atEnd = index >= count - 1;

  if (variant === 'boxed') {
    const arrow = 'flex h-full w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent';
    return (
      <div className={`flex h-10 items-center overflow-hidden rounded-xl border border-border bg-card ${className}`}>
        <button type="button" disabled={atStart} onClick={() => onIndexChange(index - 1)} title="Previous (←)" aria-label="Previous" className={arrow}>
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <span className="whitespace-nowrap px-2 text-[14px] tabular-nums text-muted-foreground">
          <span className="font-semibold text-foreground">{index + 1}</span> of {count}
        </span>
        <button type="button" disabled={atEnd} onClick={() => onIndexChange(index + 1)} title="Next (→)" aria-label="Next" className={arrow}>
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <button
        type="button"
        disabled={atStart}
        onClick={() => onIndexChange(index - 1)}
        title="Previous (←)"
        aria-label="Previous"
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
      <span className="whitespace-nowrap px-1 text-[12px] tabular-nums text-muted-foreground">
        {index + 1} of {count} {noun}
      </span>
      <button
        type="button"
        disabled={atEnd}
        onClick={() => onIndexChange(index + 1)}
        title="Next (→)"
        aria-label="Next"
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent"
      >
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
